import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react"
import ReactDOM from "react-dom"
import PropTypes from "prop-types"
import { Link } from "gatsby"
import bbox from "@turf/bbox"
import { featureCollection } from "@turf/helpers"

import Marker from "./marker"
import {
  yandexMapKey,
  mapStyle,
  defaultLocation,
  defaultZoom,
} from "./constants"
import Arrow from "./assets/arrow.inline.svg"
import styles from "./map.module.scss"

// идентификатор исчтоника данных кластера
const clusterId = "clusterer-source"

// отступ по краям при клике на клстер
const zoomPadding = 0.1

// загрузчик карты
const loadMapScript = () =>
  new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.src = `https://api-maps.yandex.ru/v3/?apikey=${yandexMapKey}&lang=ru_RU`
    script.onload = resolve
    script.onerror = reject
    document.head.appendChild(script)
  })

// функция, которая создаёт и возвращает компонент с картой
let ReactMap = () => null
const createMapComponent = async () => {
  const [ymaps3React] = await Promise.all([
    // ждём загрузки апи обёрток для реакта
    ymaps3.import("@yandex/ymaps3-reactify"),
    // ждём загрузки апи карт
    ymaps3.ready,
  ])

  const reactify = ymaps3React.reactify.bindTo(React, ReactDOM)

  const {
    YMap,
    YMapControls,
    YMapDefaultSchemeLayer,
    YMapFeatureDataSource,
    YMapLayer,
    YMapListener,
    YMapMarker,
  } = reactify.module(ymaps3)

  // ждём загрузки модуля элементов управления, для кнопок масштаба и определения позиции
  const { YMapZoomControl, YMapGeolocationControl } = reactify.module(
    await ymaps3.import("@yandex/ymaps3-controls@0.0.1")
  )

  // ждём загрузки модуля кластера
  const { YMapClusterer, clusterByGrid } = reactify.module(
    await ymaps3.import("@yandex/ymaps3-clusterer@0.0.1")
  )

  // собственно компонент карт
  // принимает points (см пропсы в концы файла)
  return forwardRef(({ points, behaviors, onClusterClick }, ref) => {
    // функция для получения размера, после которого точки слепляются в кластер
    const gridSizedMethod = useMemo(() => clusterByGrid({ gridSize: 64 }), [])
    const [selected, setSelected] = useState(null)
    const [location, setLocation] = useState({
      center: defaultLocation,
      zoom: defaultZoom,
    })

    // меняем масштаб карты и положение карты при клике на кластер
    const handleClusterZoom = features => {
      const [minX, minY, maxX, maxY] = bbox(featureCollection(features))
      setLocation({
        bounds: [
          [minX - zoomPadding, minY - zoomPadding],
          [maxX + zoomPadding, maxY + zoomPadding],
        ],
      })
    }

    // отдаём наружу метод для зума карты по переданным точкам
    useImperativeHandle(ref, () => ({
      zoomToBounds: boundsPoints => {
        handleClusterZoom(
          (boundsPoints || []).map(point => ({
            geometry: {
              type: "Point",
              coordinates: point.coordinates,
            },
          }))
        )
      },
    }))
    //
    // useEffect(() => {
    //   ref.current?.zoomToBounds?.(points)
    // }, [points])

    // функция для создания элемента маркера
    const marker = useCallback(
      feature => (
        <YMapMarker
          key={feature.id}
          coordinates={feature.geometry.coordinates}
          source={clusterId}
          zIndex={selected === feature.id ? 999 : 0}
        >
          <Marker
            id={feature.id}
            content={feature.content}
            selected={selected}
            onSelect={setSelected}
          />
        </YMapMarker>
      ),
      [selected]
    )

    // функция для создания элемента кластера
    const cluster = useCallback(
      (coordinates, features) => (
        <YMapMarker
          key={`${features[0].id}-${features.length}`}
          coordinates={coordinates}
          source={clusterId}
          onClick={() => {
            handleClusterZoom(features)
            onClusterClick?.()
          }}
          activeZoneMargin={[50, 50, 50, 50]}
        >
          <div className={styles.cluster}>{features.length}</div>
        </YMapMarker>
      ),
      []
    )

    return (
      <YMap location={location} behaviors={behaviors}>
        {/* слой с картой и своим "серым" стилем */}
        <YMapDefaultSchemeLayer customization={mapStyle} />
        {/* слой кластера */}
        <YMapFeatureDataSource id={clusterId} />
        {/* слой с маркерами из кластера  */}
        <YMapLayer source={clusterId} type="markers" />
        {/* сам кластеризатор */}
        <YMapClusterer
          marker={marker}
          cluster={cluster}
          method={gridSizedMethod}
          features={(points || []).map((point) => ({
            type: "Feature",
            id: point.id,
            geometry: {
              type: "Point",
              coordinates: point.coordinates,
            },
            content: point.content,
          }))}
        />
        {/*  слой с элементами управления */}
        <YMapControls position="right">
          {/* элемент управление масштабом */}
          <YMapZoomControl />
          {/* элемент геопозции */}
          <YMapGeolocationControl className={styles.geo} />
        </YMapControls>
        {/* прослушивание событий */}
        <YMapListener
          onUpdate={({ location }) => {
            setLocation(location)
          }}
        />
      </YMap>
    )
  })
}

const Map = forwardRef(
  ({ children, points, onClusterClick, language }, ref) => {
    const [mapState, setMapState] = useState("loading") // loading|error|success

    // зум карты при скролле страницы отключен, включение зума по клику на карту
    // при повторном клике вне карты зум при скролле снова отключен
    const initialBehaviors = ['drag', 'pinchZoom', 'dblClick'];
    const [behaviors, setBehaviors] = useState(initialBehaviors);
    const init = async () => {
      try {
        setMapState("loading")

        // добавляем скрипт-загрузчик
        if (!window.ymaps3) {
          await loadMapScript()
        }

        // ждём создания карты
        ReactMap = await createMapComponent()

        setMapState("success")
      } catch (e) {
        setMapState("error")
      }
    }

    const handleClick = (event) => {
      if (event.target.localName === 'canvas') {
        setBehaviors([...initialBehaviors, 'scrollZoom']);
      } else {
        setBehaviors(initialBehaviors);
      }
    }

    useEffect(() => {
      init()
      document.addEventListener('click', handleClick);

      return () => {
        document.removeEventListener("click", handleClick);
      }
    }, []);

    return (
      <div className={styles.mapLayer}>
        {mapState !== "success" && (
          <div className={styles.text}>
            {mapState === "error" && (
              <>
                Нет данных для
                <br />
                формирования карты акций
                <Link to={`/${language}/`} className={styles.link}>
                  Вернуться на главную <Arrow />
                </Link>
              </>
            )}
            {mapState === "loading" && "Подождите, карта загружается"}
          </div>
        )}
        {Boolean(ReactMap) && mapState === "success" && (
          <>
            <ReactMap
              points={points}
              behaviors={behaviors}
              onClusterClick={onClusterClick}
              ref={ref}
            />
            {children}
          </>
        )}
      </div>
    )
  }
)

Map.propTypes = {
  children: PropTypes.node.isRequired,
  points: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      content: PropTypes.node.isRequired,
      coordinates: PropTypes.arrayOf(PropTypes.number),
    })
  ),
  onClusterClick: PropTypes.func,
}

export default Map
